/** @jsxImportSource theme-ui @jsxRuntime classic */
import { H } from '@hzdg/sectioning';
import React, { useRef } from 'react';
import { ThemeUIStyleObject } from 'theme-ui';

import {
  Flex,
  Container,
  SxStyleProp,
  Link,
  CarouselV2ContextProvider,
  CarouselV2,
  CarouselNavigationV2,
  useThemeUIValue,
} from '..';

import { ImageCarouselCard } from './ImageCarouselCard';

import type { ImageCarouselCardProps } from './ImageCarouselCard';

export interface ImageCardCarouselProps {
  title?: string | JSX.Element;
  data: ImageCarouselCardProps[];
  sx?: ThemeUIStyleObject;
  className?: string;
  carouselTitleSx?: SxStyleProp;
  seeAllLink?: {
    url: string;
    label?: string;
  };
}

/**
 * `ImageCardCarousel` will render a list of `data` in a `CarouselList`
 * with a `title` and a `CarouselNavigation`.
 *
 * @see https://tishman.netlify.app/components/carousel
 */
export function ImageCardCarousel({
  data,
  title = 'More Upcoming Events',
  sx,
  className,
  carouselTitleSx,
  seeAllLink,
}: ImageCardCarouselProps): React.JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  const slideSpaceBetween = useThemeUIValue([3, 4], 'space');

  const containerWidth =
    ref.current?.querySelector("div[data-module='CarouselList'] .swiper")
      ?.clientWidth || 0;

  const slidesPerView = slideSpaceBetween
    ? containerWidth / (346 + slideSpaceBetween)
    : 1;

  return (
    <Flex
      className={className}
      data-module="ImageCardCarousel"
      ref={ref}
      sx={{
        flexDirection: 'column',
        position: 'relative',
        pt: [4, 4, 7],
        pb: [4, 6],
        ...sx,
      }}
    >
      <Container px={[3, 4]}>
        <H
          sx={{
            variant: 'styles.h1',
            visibility: ['hidden', 'hidden', 'visible'],
            fontSize: 4, // FIXME: mirrors styles.h1, but using the variant won't get the 7 value to appear
            mb: [3, 5],
            fontFamily: 'headingSecondary',
            ...carouselTitleSx,
          }}
        >
          {title}
        </H>
      </Container>
      <CarouselV2ContextProvider>
        <CarouselV2
          name={typeof title === 'string' ? title : undefined}
          slideStyle={{
            maxWidth: '346px',
            minWidth: '346px',
          }}
          swiperOptions={{
            slidesPerView,
            style: {
              paddingLeft: slideSpaceBetween,
              paddingRight: slideSpaceBetween,
              maxWidth: '1200px',
            },
          }}
          virtualWindow={5}
        >
          {data.map((item, index) => (
            <ImageCarouselCard key={item.id || index} {...item} />
          ))}
        </CarouselV2>
        <Container
          sx={{
            alignSelf: 'center',
            display: 'flex',
            justifyContent: 'flex-end',
            mt: [4, 0],
            mb: [3, 0],
            position: ['static', 'static', 'absolute'],
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            {seeAllLink && (
              <Link href={seeAllLink.url} sx={{ mr: 32 }} variant="underline">
                {seeAllLink.label || 'See Them All'}
              </Link>
            )}
            <CarouselNavigationV2 />
          </div>
        </Container>
      </CarouselV2ContextProvider>
    </Flex>
  );
}
